import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Article } from '@/types/article';
import { useAuth } from '@/hooks/useClerkAuth';
import { deleteArticle } from '@/services/articles';
import { useToast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Clock,
  BookOpen,
  Image as ImageIcon,
  ExternalLink,
  Trash2,
} from 'lucide-react';

interface ArticleCardProps {
  article: Article;
  onClick: () => void;
  onDelete?: (articleId: string) => void;
}

const ArticleCard = ({ article, onClick, onDelete }: ArticleCardProps) => {
  const { t } = useTranslation();
  const { canDeleteArticle } = useAuth();
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await deleteArticle(article.id);
      onDelete(article.id);
      toast({
        title: t('articleCard.deleteSuccess'),
        description: t('articleCard.deleteSuccessDescription'),
      });
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        title: t('articleCard.deleteFailed'),
        description: t('articleCard.deleteFailedDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card
      className="bg-gradient-card shadow-card hover:shadow-elevated transition-all duration-300 cursor-pointer hover:-translate-y-1 border-learning-muted/30 overflow-hidden"
      onClick={onClick}
    >
      {article.imageUrl && (
        <div className="aspect-video w-full overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
      )}

      {!article.imageUrl && (
        <div className="aspect-video w-full bg-gradient-to-br from-learning-muted/20 to-learning-muted/40 flex items-center justify-center">
          <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
        </div>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-foreground text-lg font-semibold line-clamp-2">
              {article.title}
            </CardTitle>
            {article.description && (
              <CardDescription className="text-muted-foreground text-sm line-clamp-2">
                {article.description}
              </CardDescription>
            )}
          </div>
          {canDeleteArticle() && onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={e => e.stopPropagation()}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                  title={t('articleCard.deleteArticle')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent onClick={e => e.stopPropagation()}>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('articleCard.deleteTitle')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('articleCard.deleteConfirmation', {
                      title: article.title,
                    })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting
                      ? t('articleCard.deletingButton')
                      : t('articleCard.deleteButton')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span>
                {article.sentences.length} {t('articleCard.sentences')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>
                {Math.ceil(article.sentences.length / 10)}{' '}
                {t('articleCard.minRead')}
              </span>
            </div>
          </div>
          {article.originalLink && (
            <Button
              size="sm"
              variant="ghost"
              onClick={e => {
                e.stopPropagation();
                window.open(
                  article.originalLink,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
              className="h-auto p-1 text-muted-foreground hover:text-foreground"
              title={t('articleCard.viewOriginalSource')}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <Badge
            variant="secondary"
            className="bg-learning-muted text-learning-primary"
          >
            {t('articleCard.german')}
          </Badge>
          <Badge
            variant="secondary"
            className="bg-learning-muted text-learning-secondary"
          >
            {t('articleCard.english')}
          </Badge>
          <Badge
            variant="secondary"
            className="bg-learning-muted text-learning-accent"
          >
            {t('articleCard.vietnamese')}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArticleCard;
